import { normalizeTag } from './notes.js';

// Free tags on tasks and files (the same grammar as note tags and the books' line-item tags):
// lowercase, dashes for spaces, 40 chars, at most 12 per item. Pure.
export const MAX_TAGS = 12;

/** Every tag used across `items` (their `field` array), most used first. */
export function tagVocab(items, field = 'tags') {
  const m = new Map();
  for (const it of items || []) for (const t of (it && Array.isArray(it[field]) ? it[field] : [])) if (t) m.set(t, (m.get(t) || 0) + 1);
  return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([t]) => t);
}

/** Suggestions for the tag input: vocab minus what's on the item, filtered by the typed text. */
export function suggestTags(vocab, have = [], term = '', n = 8) {
  const q = normalizeTag(term);
  const on = new Set(have);
  return (vocab || []).filter((t) => !on.has(t) && (!q || t.includes(q))).slice(0, n);
}

/** Add one tag (normalised, no duplicates, capped). Returns the same array when nothing changes. */
export function withTag(tags, raw) {
  const t = normalizeTag(raw);
  const cur = Array.isArray(tags) ? tags : [];
  if (!t || cur.includes(t) || cur.length >= MAX_TAGS) return cur;
  return [...cur, t];
}

// ── The suite's one tag list (CFO plan 4.1) ──────────────────────────────────────────────────────────────────────
// The books' tagVocab is the master; they publish it to `tags/{slug}` (opsTags), read-only here. A doc:
// { slug, name, kind, revenueAccountId, color, pending, mergedInto (the target's slug) }. Tags on tasks and
// projects stay as typed; a merged tag maps to its target when read (resolveTag) — docs are never rewritten in bulk.
export const TAG_KINDS = ['trade', 'work', 'internal', 'channel', 'equipment', 'personal'];
export const TAG_KIND_LABELS = { trade: 'Trade', work: 'Work', internal: 'Internal', channel: 'Lead channel', equipment: 'Equipment', personal: 'Personal' };

/** The suite's key for a tag (mirrored in the books and bids): lowercase, runs of anything not a-z / 0-9 → '-', 40 chars. */
export const canonTag = (t) => String(t ?? '').trim().replace(/^#/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '').slice(0, 40).replace(/-+$/, '');

/** slug → opsTags doc. Accepts the docs (or an index already built). */
export function tagIndex(opsTags) {
  if (opsTags instanceof Map) return opsTags;
  const m = new Map();
  for (const t of opsTags || []) { const k = t && (t.slug || t.id); if (k) m.set(k, t); }
  return m;
}

/** What a tag reads as: a merged tag's target slug (followed to the end, loops stop), else the tag as it is. */
export function resolveTag(tag, opsTags) {
  if (!tag) return tag;
  const idx = tagIndex(opsTags);
  let slug = canonTag(tag);
  let d = idx.get(slug);
  if (!d || !d.mergedInto) return tag;
  const seen = new Set([slug]);
  while (d && d.mergedInto && !seen.has(d.mergedInto)) { slug = d.mergedInto; seen.add(slug); d = idx.get(slug); }
  return slug;
}

/** A tag list read through the suite list: merged tags → their targets, no duplicates (by slug). */
export function resolveTags(tags, opsTags) {
  const idx = tagIndex(opsTags);
  const out = [];
  const seen = new Set();
  for (const t of Array.isArray(tags) ? tags : []) {
    const r = resolveTag(t, idx);
    const k = canonTag(r);
    if (!r || seen.has(k)) continue;
    seen.add(k); out.push(r);
  }
  return out;
}

/** What a tag picker offers: the suite's live tags first (those used here first), then the local vocab, read through merges. */
export function suiteVocab(opsTags, local = []) {
  const idx = tagIndex(opsTags);
  const rank = new Map();
  (local || []).forEach((t, i) => { const k = canonTag(resolveTag(t, idx)); if (!rank.has(k)) rank.set(k, i); });
  const live = [...idx.values()].filter((t) => !t.mergedInto && (t.slug || t.id))
    .sort((a, b) => (rank.get(a.slug || a.id) ?? Infinity) - (rank.get(b.slug || b.id) ?? Infinity) || String(a.slug || a.id).localeCompare(String(b.slug || b.id)));
  const out = [];
  const seen = new Set();
  const push = (t) => { const k = canonTag(t); if (t && k && !seen.has(k)) { seen.add(k); out.push(t); } };
  for (const t of live) push(t.slug || t.id);
  for (const t of local || []) push(resolveTag(t, idx));
  return out;
}
